import { motion } from "framer-motion";
import { useLang } from "@/i18n";
import { MANIFESTO } from "@/manifesto";
import Reveal from "@/components/Reveal";

const IMG_MAIN =
  "https://images.unsplash.com/photo-1776623051149-2beef680b647?auto=format&fit=crop&q=85&w=1200";
const IMG_OVERLAP =
  "https://images.unsplash.com/photo-1727081979210-ff973479b458?auto=format&fit=crop&q=85&w=800";

export default function Story() {
  const { t, lang } = useLang();
  const s = t.story;
  const m = MANIFESTO[lang] || MANIFESTO.fr;

  return (
    <section id="histoire" data-testid="story-section" className="scroll-mt-24 bg-paper py-24 md:py-36">
      <div className="mx-auto grid max-w-7xl items-center gap-16 px-5 md:px-10 lg:grid-cols-2 lg:gap-24">
        <div>
          <Reveal>
            <p className="text-xs font-semibold uppercase tracking-[0.28em] text-wood">{s.eyebrow}</p>
            <h2 className="mt-4 font-serif text-4xl font-light leading-tight tracking-tight text-pine md:text-5xl">
              {s.title}
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-8 text-base leading-relaxed text-pine/75 md:text-lg">{s.p1}</p>
            <p className="mt-5 text-base leading-relaxed text-pine/75 md:text-lg">{s.p2}</p>
          </Reveal>
          <Reveal delay={0.2}>
            <blockquote className="mt-10 border-l-2 border-wood pl-6">
              <p className="font-serif text-xl italic leading-snug text-pine md:text-2xl">{s.quote}</p>
              <cite className="mt-3 block text-xs font-semibold uppercase tracking-[0.2em] not-italic text-pine/60">
                {s.quoteAuthor}
              </cite>
            </blockquote>
          </Reveal>
          <Reveal delay={0.3}>
            <div className="mt-12 grid grid-cols-2 gap-6 border-t border-pine/10 pt-8 sm:grid-cols-3 md:grid-cols-5">
              {s.stats.map((stat, i) => (
                <div key={i} data-testid={`story-stat-${i}`}>
                  <p className="whitespace-nowrap font-serif text-3xl font-light text-pine md:text-4xl">{stat.value}</p>
                  <p className="mt-2 text-[11px] font-semibold uppercase tracking-[0.16em] text-pine/55">
                    {stat.label}
                  </p>
                </div>
              ))}
            </div>
          </Reveal>
        </div>

        <div className="relative">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-70px" }}
            transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
            className="group overflow-hidden rounded-2xl"
          >
            <img
              src={IMG_MAIN}
              alt="Sommets du massif de Belledonne"
              className="aspect-[4/5] w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
            />
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 70 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-70px" }}
            transition={{ duration: 1, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
            className="group absolute -bottom-10 -left-6 w-2/5 overflow-hidden rounded-2xl border-8 border-paper shadow-xl md:-left-14"
          >
            <img
              src={IMG_OVERLAP}
              alt="Intérieur chaleureux du restaurant"
              className="aspect-square w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
            />
          </motion.div>
        </div>
      </div>

      <Reveal className="mx-auto mt-24 max-w-7xl px-5 md:mt-32 md:px-10">
        <div
          data-testid="story-manifesto"
          className="relative overflow-hidden rounded-3xl bg-pine px-8 py-12 text-paper md:px-16 md:py-20"
        >
          <span aria-hidden="true" className="pointer-events-none absolute -right-6 -top-10 font-serif text-[180px] italic leading-none text-paper/5 select-none">
            Ô
          </span>
          <p className="text-xs font-semibold uppercase tracking-[0.28em] text-wood-light">
            {m.label}
          </p>
          <div className="mt-8 max-w-3xl space-y-6">
            {m.lines.map((line, i) => (
              <p
                key={i}
                className={`font-serif font-light leading-snug tracking-tight ${
                  i === 0 ? "text-3xl italic md:text-4xl" : "text-xl text-paper/85 md:text-2xl"
                }`}
              >
                {line}
              </p>
            ))}
          </div>
          <div data-testid="manifesto-author" className="mt-10 flex items-center gap-4 border-t border-paper/15 pt-6">
            <img
              src="/images/jean-philippe.jpg"
              alt="Jean-Philippe Vacher"
              className="h-14 w-14 rounded-full border-2 border-wood-light/60 object-cover"
            />
            <div>
              <p className="font-serif text-xl text-paper">{m.author}</p>
              <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-wood-light">
                {m.role}
              </p>
            </div>
          </div>
        </div>
      </Reveal>
    </section>
  );
}
